import AlbumService from "./AlbumService";
import AlbumImageUrlCache from "./AlbumImageUrlCache";

const DEFAULT_URL_TTL_MS = 30 * 60 * 1000;

function normalizeExpiry(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0
        ? (value < 100000000000 ? value * 1000 : value)
        : null;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) return normalizeExpiry(Number(value));
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function getExpiryFromUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const date = parsedUrl.searchParams.get("X-Amz-Date");
    const expires = Number(parsedUrl.searchParams.get("X-Amz-Expires"));
    if (!date || !Number.isFinite(expires) || expires <= 0) return null;
    const isoDate = date.replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
        "$1-$2-$3T$4:$5:$6Z",
    );
    const startedAt = Date.parse(isoDate);
    return Number.isNaN(startedAt) ? null : startedAt + expires * 1000;
  } catch (error) {
    return null;
  }
}

class AlbumImageUrlService {
  constructor() {
    this.cache = AlbumImageUrlCache;
    this.inflight = new Map();
  }

  needsPresignedUrl(picture) {
    if (!picture) return false;
    const storageStatus = String(
        picture.storageStatus || picture.status || picture.status_S3_SV || "",
    ).toUpperCase();
    if (storageStatus === "S3_SV" || storageStatus === "SERVER") return false;
    return storageStatus === "MI_SV" || storageStatus === "MI_0" || Boolean(picture.urlMinio);
  }

  getObjectKey(picture) {
    if (!picture) return "";
    return picture.objectKey || picture.urlMinio || "";
  }

  getCachedUrl(picture) {
    if (!this.needsPresignedUrl(picture)) return "";
    const objectKey = this.getObjectKey(picture);
    if (!objectKey) return "";
    const cached = this.cache.get(objectKey);
    return cached ? cached.url : "";
  }

  async resolve(picture, {force = false} = {}) {
    const objectKey = this.getObjectKey(picture);
    if (!this.needsPresignedUrl(picture) || !objectKey) {
      return picture && (picture.urlPictureShow || picture.urlPicture || picture.urlOriginal || "");
    }

    const context = this.cache.getContext();
    const cached = this.cache.get(objectKey);
    if (!force && cached) {
      return cached.url;
    }

    const requestKey = JSON.stringify([context.scope, context.generation, objectKey]);
    if (this.inflight.has(requestKey)) {
      return this.inflight.get(requestKey);
    }

    this.cache.delete(objectKey);
    const requestedAt = Date.now();
    const request = AlbumService.getAlbumPictureViewUrlV2(objectKey)
        .then((response) => {
          const payload = response.data && response.data.data != null ? response.data.data : response.data;
          const url = typeof payload === "string"
              ? payload
              : payload && (payload.url || payload.previewUrl || payload.downloadUrl ||
                  payload.presignedUrl || payload.urlPictureShow);
          if (!url) throw new Error("Gateway không trả về presigned URL ảnh album");
          const expiresAt = normalizeExpiry(payload && (payload.expiredAt || payload.expiresAt ||
              payload.expireAt || payload.expirationTime)) || getExpiryFromUrl(url) ||
              requestedAt + DEFAULT_URL_TTL_MS;
          this.cache.set(objectKey, {url, expiresAt}, context);
          return url;
        })
        .finally(() => {
          if (this.inflight.get(requestKey) === request) this.inflight.delete(requestKey);
        });
    this.inflight.set(requestKey, request);
    return request;
  }

  invalidate(picture) {
    const objectKey = this.getObjectKey(picture);
    if (objectKey) this.cache.delete(objectKey);
  }
}

export default new AlbumImageUrlService();
